// Simple in-memory cache shared across biller pages for the lifetime of the session.
// Each entry stores { data, timestamp } so callers can enforce their own TTL.

const _cache = new Map();

/**
 * Returns the cached value for `key` if it exists and has not exceeded `ttlMs`.
 * Returns null when the entry is missing or stale.
 */
export function getCacheEntry(key, ttlMs) {
    const entry = _cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > ttlMs) {
        _cache.delete(key);
        return null;
    }
    return entry.data;
}

/**
 * Stores `data` under `key` with the current timestamp.
 */
export function setCacheEntry(key, data) {
    _cache.set(key, { data, timestamp: Date.now() });
}

// TTL for service ID list: 10 minutes (rarely changes within a session)
const SERVICE_IDS_TTL = 10 * 60 * 1000;

/**
 * Returns the service ID array for the given practice, fetching from the API
 * on first call and returning the cached result on subsequent calls.
 * Returns [] on error so callers can proceed without service filtering.
 */
export async function getOrFetchServiceIDs(practiceID) {
    const key = `service-ids:${practiceID}`;
    const cached = getCacheEntry(key, SERVICE_IDS_TTL);
    if (cached !== null) return cached;

    try {
        const res = await fetch(
            `/api/core/HealthivaWebServices/HealthivaGenralUtilityWS.asmx/GetCPTServiceNames`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    sInputJSON: JSON.stringify({ sPracticeID: practiceID }),
                }),
            }
        );
        const json = await res.json();
        const parsed = typeof json.d === "string" ? JSON.parse(json.d) : json.d;
        const ids = (Array.isArray(parsed) ? parsed : [])
            .map((s) => s.ServiceID)
            .filter(Boolean);
        setCacheEntry(key, ids);
        return ids;
    } catch {
        return [];
    }
}
